import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Format number as Indian currency string
const formatINR = (amount) => {
  if (amount === null || amount === undefined) return '₹0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₹0.00';
  return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Format a plain number (e.g. txn count) with locale separators, no decimals
const formatCount = (val) => {
  if (val === null || val === undefined) return '0';
  const num = Math.round(parseFloat(val));
  if (isNaN(num)) return '0';
  return num.toLocaleString('en-IN');
};

const ITEMS_PER_PAGE = 20;

const ZeerowInvoice = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  // Category selection state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [checkedCategories, setCheckedCategories] = useState(new Set());
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    fetchInvoice();
    setPage(1);
    setSearchQuery('');
  }, [month, year]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/v1/admin/zeerow/invoices?month=${month}&year=${year}`);
      setInvoice(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setInvoice(null);
      } else {
        const msg = err.response?.data?.error || 'Failed to fetch invoice';
        setError(msg);
        toast.error(msg);
        setInvoice(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories whenever the modal opens
  useEffect(() => {
    if (!showCategoryModal) return;
    let cancelled = false;

    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await api.get(
          `/api/v1/admin/zeerow/invoices/txn-categories?month=${month}&year=${year}`
        );
        if (!cancelled) {
          const cats = response.data || [];
          setCategories(cats);
          setCheckedCategories(new Set(cats)); // all checked by default
        }
      } catch (err) {
        if (!cancelled) {
          setCategories([]);
          setCheckedCategories(new Set());
        }
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };

    fetchCategories();
    return () => { cancelled = true; };
  }, [showCategoryModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: validate month, then open modal immediately
  const handleOpenGenerateModal = () => {
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    if (year > currentYear || (year === currentYear && month >= currentMonth)) {
      const msg = 'Invoice can only be generated for completed past months.';
      setError(msg);
      toast.error(msg);
      return;
    }

    // Reset and open — categories are fetched inside the modal via useEffect
    setCategories([]);
    setCheckedCategories(new Set());
    setShowCategoryModal(true);
  };

  const toggleCategory = (cat) => {
    setCheckedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const selectAll   = () => setCheckedCategories(new Set(categories));
  const deselectAll = () => setCheckedCategories(new Set());

  // Step 2: confirm generate with excluded categories
  const handleGenerate = async () => {
    setShowCategoryModal(false);

    // Excluded = categories that are NOT checked
    const excludedCategories = categories.filter((c) => !checkedCategories.has(c));

    try {
      setGenerating(true);
      setError('');
      const response = await api.post('/api/v1/admin/zeerow/invoices/generate', {
        month,
        year,
        excludedCategories,
      });
      setInvoice(response.data);
      toast.success('Zeerow invoice generated successfully');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate invoice';
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleExportCSV = async () => {
    if (!invoice?.id) return;
    try {
      setExporting(true);
      const response = await api.get(`/api/v1/admin/zeerow/invoices/${invoice.id}/export-csv`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zeerow-invoice-${year}-${String(month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to export CSV';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  // Filtered breakdown for search
  const breakdown = invoice?.breakdown || [];
  const filteredBreakdown = useMemo(() => {
    if (!searchQuery.trim()) return breakdown;
    const q = searchQuery.trim().toLowerCase();
    return breakdown.filter((row) =>
      (row.enterpriseName || '').toLowerCase().includes(q)
    );
  }, [breakdown, searchQuery]);

  const totalBreakdownPages = Math.ceil(filteredBreakdown.length / ITEMS_PER_PAGE);
  const paginatedBreakdown = filteredBreakdown.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // Compute totals row for breakdown
  const totalsRow = useMemo(() => {
    return breakdown.reduce(
      (acc, row) => ({
        kycMakerCount:      acc.kycMakerCount      + (row.kycMakerCount || 0),
        upiTxnCount:        acc.upiTxnCount        + (row.upiTxnCount || 0),
        totalTxnAmount:     acc.totalTxnAmount     + parseFloat(row.totalTxnAmount || 0),
        kycMakerCharge:     acc.kycMakerCharge     + parseFloat(row.kycMakerCharge || 0),
        upiCharge:          acc.upiCharge          + parseFloat(row.upiCharge || 0),
        txnPercentageCharge:acc.txnPercentageCharge+ parseFloat(row.txnPercentageCharge || 0),
        totalCharge:        acc.totalCharge        + parseFloat(row.totalCharge || 0),
      }),
      { kycMakerCount: 0, upiTxnCount: 0, totalTxnAmount: 0, kycMakerCharge: 0,
        upiCharge: 0, txnPercentageCharge: 0, totalCharge: 0 }
    );
  }, [breakdown]);

  const grandTotal =
    parseFloat(invoice?.totalKycMakerCharge || 0) +
    parseFloat(invoice?.totalUpiCharge || 0) +
    parseFloat(invoice?.totalTxnPercentageCharge || 0);

  const statusBadge = (status) => {
    const map = {
      GENERATED: 'bg-green-100 text-green-700',
      PENDING:   'bg-yellow-100 text-yellow-700',
      FAILED:    'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>
        {status || '—'}
      </span>
    );
  };

  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-6 py-8 space-y-6">

        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Zeerow Combined Invoice</h1>
            <p className="text-sm text-gray-500 mt-1">View and generate monthly invoices for all Zeerow enterprises</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Month selector */}
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            >
              {MONTH_NAMES.map((name, idx) => (
                <option key={idx + 1} value={idx + 1}>{name}</option>
              ))}
            </select>

            {/* Year selector */}
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            {/* Export CSV */}
            <button
              onClick={handleExportCSV}
              disabled={!invoice || exporting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>

            {/* Generate — opens category selection modal immediately */}
            <button
              onClick={handleOpenGenerateModal}
              disabled={generating || loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {generating ? 'Generating...' : 'Generate Zeerow Account Invoice'}
            </button>

            {/* Config shortcut */}
            <button
              onClick={() => navigate('/admin/zeerow/config')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Billing Config
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-lg leading-none">&times;</button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-sm">Loading invoice...</p>
          </div>
        )}

        {/* No invoice state */}
        {!loading && !invoice && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium">No invoice found</p>
            <p className="text-sm text-gray-400 mt-1">
              No Zeerow invoice exists for {MONTH_NAMES[month - 1]} {year}.
              Click "Generate Zeerow Account Invoice" to create one.
            </p>
          </div>
        )}

        {/* Invoice content */}
        {!loading && invoice && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Zeerow Enterprises</p>
                <p className="text-2xl font-bold text-gray-900">{invoice.totalZeerowEnterprises ?? '—'}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Total KYC Makers</p>
                <p className="text-2xl font-bold text-gray-900">{invoice.totalKycMakerCount?.toLocaleString('en-IN') ?? '—'}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Total UPI Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{invoice.totalUpiTxnCount?.toLocaleString('en-IN') ?? '—'}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatINR(invoice.totalAmount)}</p>
              </div>
            </div>

            {/* Invoice totals card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-800">Invoice Breakdown</h2>
                  {invoice.invoiceNumber && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Invoice #: <span className="font-medium text-gray-600">{invoice.invoiceNumber}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(invoice.status)}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase bg-gray-50">
                      <th className="text-left px-4 py-2 font-medium">Charge Type</th>
                      <th className="text-left px-4 py-2 font-medium">Calculation</th>
                      <th className="text-right px-4 py-2 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700 font-medium">KYC Maker Charge</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {invoice.totalKycMakerCount?.toLocaleString('en-IN')} makers × ₹{parseFloat(invoice.kycMakerRateSnapshot || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium text-right">{formatINR(invoice.totalKycMakerCharge)}</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700 font-medium">UPI Charge</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {invoice.totalUpiTxnCount?.toLocaleString('en-IN')} transactions × ₹{parseFloat(invoice.upiChargeRateSnapshot || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium text-right">{formatINR(invoice.totalUpiCharge)}</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700 font-medium">Transaction % Charge</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {formatCount(invoice.totalTxnAmount)} txns × {parseFloat(invoice.txnPercentageSnapshot || 0).toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium text-right">{formatINR(invoice.totalTxnPercentageCharge)}</td>
                    </tr>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-3 text-gray-900" colSpan={2}>Grand Total</td>
                      <td className="px-4 py-3 text-gray-900 text-right">{formatINR(grandTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per-enterprise breakdown table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Per-Enterprise Breakdown</p>
                  <p className="text-xs text-gray-400 mt-0.5">{breakdown.length} enterprise{breakdown.length === 1 ? '' : 's'}</p>
                </div>
                <input
                  type="text"
                  placeholder="Search by enterprise name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-64"
                />
              </div>

              {filteredBreakdown.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-gray-400">
                    {searchQuery ? 'No enterprises match your search.' : 'No enterprise data available.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-gray-400 uppercase bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium whitespace-nowrap">Enterprise Name</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">KYC Makers</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">UPI Txns</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Total Txn Count</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Maker Chg</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">UPI Chg</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Txn % Chg</th>
                          <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Total</th>
                          <th className="text-left px-4 py-3 font-medium whitespace-nowrap">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {paginatedBreakdown.map((row, idx) => (
                          <tr key={row.enterpriseId || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-800 font-medium">{row.enterpriseName || '—'}</td>
                            <td className="px-4 py-3 text-gray-700 text-right">{(row.kycMakerCount || 0).toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-gray-700 text-right">{(row.upiTxnCount || 0).toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-gray-700 text-right">{formatCount(row.totalTxnAmount)}</td>
                            <td className="px-4 py-3 text-gray-700 text-right">{formatINR(row.kycMakerCharge)}</td>
                            <td className="px-4 py-3 text-gray-700 text-right">{formatINR(row.upiCharge)}</td>
                            <td className="px-4 py-3 text-gray-700 text-right">{formatINR(row.txnPercentageCharge)}</td>
                            <td className="px-4 py-3 text-gray-800 font-semibold text-right">{formatINR(row.totalCharge)}</td>
                            <td className="px-4 py-3">
                              {row.errorMessage ? (
                                <span
                                  className="inline-block px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium"
                                  title={row.errorMessage}
                                >
                                  Error
                                </span>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {/* TOTAL row — pinned at bottom, covers all enterprises (not just current page) */}
                      <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                        <tr className="font-semibold text-gray-900">
                          <td className="px-4 py-3">TOTAL</td>
                          <td className="px-4 py-3 text-right">{totalsRow.kycMakerCount.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right">{totalsRow.upiTxnCount.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right">{formatCount(totalsRow.totalTxnAmount)}</td>
                          <td className="px-4 py-3 text-right">{formatINR(totalsRow.kycMakerCharge)}</td>
                          <td className="px-4 py-3 text-right">{formatINR(totalsRow.upiCharge)}</td>
                          <td className="px-4 py-3 text-right">{formatINR(totalsRow.txnPercentageCharge)}</td>
                          <td className="px-4 py-3 text-right">{formatINR(totalsRow.totalCharge)}</td>
                          <td className="px-4 py-3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {totalBreakdownPages > 1 && (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400">
                        Showing {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, filteredBreakdown.length)} of {filteredBreakdown.length}
                      </p>
                      <div className="flex items-center gap-1">
                        <button
                          disabled={page === 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          className="px-2 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.min(5, totalBreakdownPages) }, (_, i) => {
                          const p = Math.max(1, Math.min(page - 2, totalBreakdownPages - 4)) + i;
                          return (
                            <button
                              key={p}
                              onClick={() => setPage(p)}
                              className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                                p === page
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {p}
                            </button>
                          );
                        })}
                        <button
                          disabled={page === totalBreakdownPages}
                          onClick={() => setPage((p) => Math.min(totalBreakdownPages, p + 1))}
                          className="px-2 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Category selection + confirm modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Generate Zeerow Invoice</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {MONTH_NAMES[month - 1]} {year}
              </p>
            </div>

            {/* Warning */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm text-amber-700">
                <span className="font-semibold">Warning:</span> If an invoice already exists for this period, it will be regenerated and previous data will be overwritten.
              </p>
            </div>

            {/* Category selection */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Section header + description */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold text-gray-800">Transaction Categories</p>
                  {!loadingCategories && categories.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAll}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full hover:bg-green-100 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Select All
                      </button>
                      <button
                        onClick={deselectAll}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full hover:bg-red-100 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Deselect All
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Kindly uncheck the categories that should be excluded from the total transaction calculation.
                  The categories listed below represent all transactions recorded during the selected invoice month.
                </p>
              </div>

              {loadingCategories ? (
                <div className="py-6 text-center text-sm text-gray-400 bg-gray-50 rounded-lg">
                  <svg className="animate-spin w-5 h-5 mx-auto mb-2 text-blue-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Loading transaction categories...
                </div>
              ) : categories.length === 0 ? (
                <div className="py-4 text-center text-sm text-gray-400 bg-gray-50 rounded-lg">
                  No transaction categories found for this period.
                  All DEBIT transactions will be included.
                </div>
              ) : (
                <div className="space-y-1 max-h-56 overflow-y-auto border border-gray-100 rounded-lg p-3 bg-gray-50">
                  {categories.map((cat) => (
                    <label
                      key={cat}
                      className="flex items-center gap-2.5 cursor-pointer py-1 px-1 rounded hover:bg-white transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={checkedCategories.has(cat)}
                        onChange={() => toggleCategory(cat)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{cat}</span>
                    </label>
                  ))}
                </div>
              )}

              {!loadingCategories && categories.length > 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  {checkedCategories.size} of {categories.length} selected
                  {checkedCategories.size < categories.length && (
                    <span className="ml-1 text-amber-600">
                      ({categories.length - checkedCategories.size} excluded)
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 justify-end mt-5 pt-4 border-t border-gray-100">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={loadingCategories}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                Confirm &amp; Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZeerowInvoice;
